import {
    accessItemTypeEnum,
    accessGroupEnum,
    accessSourceTypeEnum,
    accessSourceNameEnum,
    contentCreatingRoleEnum,
    trainingAndFieldRoleEnum,
} from '../../../serverClientShared/sharedEnums';

export const EMPTY_ORIGINAL = {includeAll: new Set(), notIncludeAll: new Set()};
export const EMPTY_EDITS = {addedIncludeAll: new Set(), addedIndividual: new Set(), removed: new Set()};
export const EMPTY_MIXED = new Map();

const buildChildNestedRoute = (child) => {
    const parentRoute = child.nestedRoute || '';
    if (child.type === accessItemTypeEnum.FOLDER) return `${parentRoute}/${child.id}`;
    return parentRoute;
};

export const buildNodeInfoMap = (projects, childrenCache) => {
    const map = new Map();
    projects.forEach(p => map.set(p.id, {parentId: null, type: accessItemTypeEnum.PROJECT, nestedRoute: p.id}));
    for (const [parentId, cached] of Object.entries(childrenCache)) {
        [...(cached.folders || []), ...(cached.items || [])].forEach(child => {
            const info = {parentId, type: child.type || accessItemTypeEnum.ITEM, nestedRoute: buildChildNestedRoute(child)};
            if (child.type === accessItemTypeEnum.ITEM) {
                info.isPublished = child.isPublished !== false;
                info.permittedRoles = child.permittedRoles || null;
            }
            map.set(child.id, info);
        });
    }
    return map;
};

export const buildChildrenByParent = (nodeInfoMap) => {
    const map = new Map();
    for (const [id, info] of nodeInfoMap) {
        if (!info.parentId) continue;
        if (!map.has(info.parentId)) map.set(info.parentId, []);
        map.get(info.parentId).push(id);
    }
    return map;
};

const classifyBulkEntry = (entry, targetSet, targetCount, includeAll, notIncludeAll, mixed) => {
    const matchingTargets = entry.sources.filter(s => targetSet.has(s));
    if (matchingTargets.length === 0) return;

    if (matchingTargets.length === targetCount) {
        if (entry.isIncludeAll) includeAll.add(entry.itemId);
        else notIncludeAll.add(entry.itemId);
    } else {
        mixed.set(entry.itemId, {memberIds: matchingTargets, isIncludeAll: entry.isIncludeAll});
    }
};

export const hydrateOriginalFromBulkEntries = (entries, targetIds) => {
    const includeAll = new Set();
    const notIncludeAll = new Set();
    const mixed = new Map();
    const targetSet = new Set(targetIds);

    for (const entry of entries) {
        classifyBulkEntry(entry, targetSet, targetIds.length, includeAll, notIncludeAll, mixed);
    }
    return {includeAll, notIncludeAll, mixed};
};

const buildEntryInfo = (entry, targetIdSet, targetCount, isBulkMode, inheritedSources) => {
    const directSources = entry.sources.filter(s => targetIdSet.has(s));
    const inherited = entry.sources.filter(s => !targetIdSet.has(s));
    const isDirect = isBulkMode ? directSources.length === targetCount : directSources.length > 0;
    const mergedInherited = inherited.map(id => ({
        sourceId: id,
        sourceName: inheritedSources[id]?.sourceName || id,
    }));
    return {isDirect, isIncludeAll: entry.isIncludeAll, inherited: mergedInherited};
};

const mergeEntryInfo = (existing, built) => ({
    isDirect: existing?.isDirect || built.isDirect,
    isDirectIncludeAll: (existing?.isDirectIncludeAll) || (built.isDirect && built.isIncludeAll),
    inheritedIncludeAll: (existing?.inheritedIncludeAll) || (built.inherited.length > 0 && built.isIncludeAll),
    inheritedFrom: [...(existing?.inheritedFrom || []), ...built.inherited],
});

export const processAccessEntries = (entries, targetIds, inheritedSources, isBulkMode) => {
    const targetIdSet = new Set(targetIds);
    const newAccessMap = {};
    for (const entry of entries) {
        const built = buildEntryInfo(entry, targetIdSet, targetIds.length, isBulkMode, inheritedSources);
        newAccessMap[entry.itemId] = mergeEntryInfo(newAccessMap[entry.itemId], built);
    }
    return newAccessMap;
};

const buildGroupSources = (groups) => {
    const sources = {};
    groups.forEach(g => {
        const id = g.GroupId || g;
        sources[id] = {sourceName: g.GroupName || id, sourceType: accessSourceTypeEnum.GROUP};
    });
    sources[accessGroupEnum.WORKSPACE_PUBLIC] = {sourceName: accessSourceNameEnum.PUBLIC, sourceType: accessSourceTypeEnum.SPECIAL};
    sources[accessGroupEnum.GUEST] = {sourceName: accessSourceNameEnum.GUEST, sourceType: accessSourceTypeEnum.SPECIAL};
    return sources;
};

const buildBulkGroupSources = (targets) => {
    const sources = {};
    targets.forEach(t => {
        (t.PopulatedGroups || []).forEach(g => {
            const gId = g.GroupId || g;
            if (!sources[gId]) sources[gId] = {sourceName: g.GroupName || gId, sourceType: accessSourceTypeEnum.GROUP};
        });
    });
    sources[accessGroupEnum.WORKSPACE_PUBLIC] = {sourceName: accessSourceNameEnum.PUBLIC, sourceType: accessSourceTypeEnum.SPECIAL};
    sources[accessGroupEnum.GUEST] = {sourceName: accessSourceNameEnum.GUEST, sourceType: accessSourceTypeEnum.SPECIAL};
    return sources;
};

const buildBulkMembership = (targets) => {
    const allGroupIds = new Set();
    const membership = new Map();
    targets.forEach(t => {
        const memberGroups = t.PopulatedGroups || t.groups || [];
        memberGroups.forEach(g => {
            const gId = g.GroupId || g;
            allGroupIds.add(gId);
            if (!membership.has(gId)) membership.set(gId, new Set());
            membership.get(gId).add(t.id);
        });
    });
    return {allGroupIds, membership};
};

export const buildInheritedAccessSourcesConfig = (groups, targets, isBulkMode) => {
    if (isBulkMode) {
        const {allGroupIds, membership} = buildBulkMembership(targets);
        return {
            inheritedIds: [...allGroupIds, accessGroupEnum.WORKSPACE_PUBLIC, accessGroupEnum.GUEST],
            inheritedSources: buildBulkGroupSources(targets),
            groupMembership: membership,
        };
    }
    const groupIds = groups.map(g => g.GroupId || g);
    if (groupIds.length === 0 && targets.length === 0) {
        return {inheritedIds: [], inheritedSources: {}, groupMembership: new Map()};
    }
    return {
        inheritedIds: [...groupIds, accessGroupEnum.WORKSPACE_PUBLIC, accessGroupEnum.GUEST],
        inheritedSources: buildGroupSources(groups),
        groupMembership: new Map(),
    };
};

const isContentCreator = (member) => {
    const role = member.ContentCreationRole || contentCreatingRoleEnum.NONE;
    return role !== contentCreatingRoleEnum.NONE;
};

const canMemberSeeItem = (member, itemInfo, isRoleVisibilityEnabled) => {
    if (isContentCreator(member)) return true;
    if (!itemInfo.isPublished) return false;
    if (isRoleVisibilityEnabled && itemInfo.permittedRoles) {
        const trainingRole = member.TrainingAndFieldRole || trainingAndFieldRoleEnum.NONE;
        if (trainingRole === trainingAndFieldRoleEnum.NONE) return false;
        return !!itemInfo.permittedRoles[trainingRole];
    }
    return true;
};

export const checkItemDisabled = (nodeId, nodeInfoMap, targets, isRoleVisibilityEnabled) => {
    const info = nodeInfoMap.get(nodeId);
    if (!info || info.type !== accessItemTypeEnum.ITEM) return false;
    if (!targets?.length) return false;
    return targets.some(member => !canMemberSeeItem(member, info, isRoleVisibilityEnabled));
};

export const getItemDisabledReason = (nodeId, nodeInfoMap, targets, isRoleVisibilityEnabled) => {
    const info = nodeInfoMap.get(nodeId);
    if (!info || info.type !== accessItemTypeEnum.ITEM) return null;
    if (!targets?.length) return null;
    const hasNonCreator = targets.some(m => !isContentCreator(m));
    if (!hasNonCreator) return null;
    if (!info.isPublished) return 'unpublished';
    if (isRoleVisibilityEnabled && info.permittedRoles) {
        const blocked = targets.some(m => {
            if (isContentCreator(m)) return false;
            const role = m.TrainingAndFieldRole || trainingAndFieldRoleEnum.NONE;
            return role === trainingAndFieldRoleEnum.NONE || !info.permittedRoles[role];
        });
        if (blocked) return 'role';
    }
    return null;
};

export const isMixedMemberAccessConflict = (nodeId, mixed, edits) => {
    if (!mixed.has(nodeId)) return false;
    return !edits.addedIncludeAll.has(nodeId)
        && !edits.addedIndividual.has(nodeId)
        && !edits.removed.has(nodeId);
};
